import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import SaveIcon from "@mui/icons-material/Save";
import ForumIcon from "@mui/icons-material/Forum";
import ComentariosList from "../ui/ComentariosList";
import SeccionInformacion from "./SeccionInformacion";
import SeccionEmpleado from "./SeccionEmpleado";
import SeccionEstado from "./SeccionEstado";
import SeccionComentarioNuevo from "./SeccionComentarioNuevo";
import { obtenerRelacionFactura } from "../../helpers/transformers";
import UsuariosService from "../../../../services/usuarios.service";
import AuthService from "../../../../services/auth.service";
import Roles from "../../../../config/roles";

/**
 * Modal para ver/editar incidencias.
 *
 * Acepta dos interfaces (adaptador de compatibilidad):
 *  - Nueva (feature incidencias): incidencia, onHide, onConfirm, loading
 *  - Legacy (Inventory/Stickers/InfoOrder): noteModal, text, handleClose,
 *    handleConfirm, modalNoteMode, modalNoteAdmin
 */
const ModalIncidencia = ({
  show,
  onHide,
  incidencia,
  onConfirm,
  loading,
  // Alias legacy
  noteModal,
  text,
  handleClose,
  handleConfirm: handleConfirmLegacy,
  modalNoteMode,
  modalNoteAdmin,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Normalización de props (nueva ↔ legacy)
  const data = incidencia ?? noteModal;
  const cerrar = onHide ?? handleClose;
  const confirmar = onConfirm ?? handleConfirmLegacy;
  const infoText = incidencia?.textoModal ?? text ?? "";

  const [comentario, setComentario] = useState("");
  const [estado, setEstado] = useState("");
  const [empleadoId, setEmpleadoId] = useState("");
  const [empleadoNombre, setEmpleadoNombre] = useState("");
  const [empleados, setEmpleados] = useState([]);
  const [imagenes, setImagenes] = useState([]);
  const [enviarEmail, setEnviarEmail] = useState(false);
  const [error, setError] = useState("");

  // esAdmin se deriva de forma síncrona (localStorage) para evitar el parpadeo
  // vista→edición que ocurría al calcularlo dentro del useEffect
  const esAdmin = AuthService.getUserRoles().includes(Roles.ADMIN.role);

  useEffect(() => {
    if (!show || !data) return;

    // Reset state
    setComentario("");
    setError("");
    setImagenes([]);
    setEnviarEmail(false);
    setEstado(data.estado?.toString() || "1");

    // En edición (incidencia existente) el "Tramitado por" es quien firma el
    // comentario → siempre el usuario logueado. Solo en creación con empleado
    // preasignado (legacy) se respeta ese empleado.
    const esEdicion = !!data.id_note;

    // Cargar empleados y setear empleado actual
    const cargarEmpleados = async () => {
      try {
        const empleadosData = await UsuariosService.listarUsuariosMap();
        setEmpleados(empleadosData);

        // Auto-seleccionar usuario logueado
        const seleccionarLogueado = () => {
          const user = AuthService.getCurrentUser();
          const usuarioLogueado = user?.username?.toLowerCase() || "";
          const empleadoEncontrado = empleadosData.find(
            (emp) => emp.nombre.toLowerCase() === usuarioLogueado
          );
          if (empleadoEncontrado) {
            setEmpleadoId(empleadoEncontrado.id.toString());
            setEmpleadoNombre(empleadoEncontrado.nombre);
          }
        };

        if (!esEdicion && data.id_empleado) {
          // Creación con empleado preasignado (legacy)
          setEmpleadoId(data.id_empleado.toString());
          setEmpleadoNombre(data.nombre_empleado);
        } else {
          seleccionarLogueado();
        }
      } catch (err) {
        console.error("Error al cargar empleados:", err);
      }
    };

    cargarEmpleados();
  }, [show, data]);

  const handleComentarioChange = (e) => {
    const value = e.target.value;
    if (value.length > 250) {
      setError("El comentario no puede exceder los 250 caracteres.");
    } else {
      setComentario(value);
      setError("");
    }
  };

  const handleEmpleadoChange = (e) => {
    const id = e.target.value;
    setEmpleadoId(id);
    const empleado = empleados.find((emp) => emp.id.toString() === id);
    if (empleado) {
      setEmpleadoNombre(empleado.nombre);
    }
  };

  // Incidencia nueva: aún no tiene id_note. Va antes del early return porque
  // handleConfirm la usa; por eso data se lee con ?.
  const esNueva = !data?.id_note;

  // El correo va al cliente de la factura: solo en incidencias existentes que la
  // tengan (las de compras son de proveedor).
  const clienteFactura = obtenerRelacionFactura(data?.invoice, "ClientesModel");
  const puedeEnviarEmail = !esNueva && !!clienteFactura && data?.origen !== "purchases";

  const handleConfirm = async () => {
    if (comentario.length > 250) {
      setError("El comentario excede los 250 caracteres.");
      return;
    }

    // Si no hay comentario, no permitir guardar
    if (!comentario.trim()) {
      setError("Debes agregar un comentario.");
      return;
    }

    // Se devuelve el objeto FUSIONADO: conserva los campos originales
    // (serie, albaran, origen, codigo_articulo, tipo_note...) necesarios para el
    // flujo de creación legacy, y añade los editados.
    const datosActualizados = {
      ...data,
      id_note: data.id_note,
      note: comentario,
      comentario: comentario,
      estado: Number(estado),
      id_empleado: Number(empleadoId),
      nombre_empleado: empleadoNombre,
      imagenes,
      enviarEmail: puedeEnviarEmail && enviarEmail,
    };

    confirmar(datosActualizados);
    setImagenes([]);
  };

  if (!data) return null;

  // Edición del estado: respeta modalNoteAdmin (legacy) si viene, si no cae al rol.
  const puedeEditarEstado =
    modalNoteAdmin != null ? Boolean(Number(modalNoteAdmin)) : esAdmin;

  // Puede editar (comentario/guardar) si la incidencia está abierta o puede editar estado.
  // Se basa en el prop data.estado (no en el state) para que el modo correcto
  // esté disponible desde el primer render y no haya parpadeo vista→edición.
  const puedeEditar = Number(data.estado) === 1 || puedeEditarEstado;

  // Label del botón: recupera "e imprimir etiqueta" en contexto de stickers (legacy)
  const labelGuardar =
    Number(modalNoteMode) === 1 ? "Guardar e imprimir etiqueta" : "Guardar cambios";

  // Incidencia nueva (aún sin id) → el empleado es el CREADOR ("Creado por").
  // Incidencia existente → el empleado solo es el autor del comentario ("Tramitado por"),
  // no modifica el creador de la incidencia.
  const labelEmpleado = esNueva ? "Creado por" : "Tramitado por";

  return (
    <Dialog
      open={show}
      onClose={loading ? undefined : cerrar}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          m: isMobile ? 0 : 2,
          // Evita el segundo scroll a nivel del Paper: solo scrollea el DialogContent
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 1, sm: 2 },
          pb: { xs: 1.5, sm: 2 },
          pt: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
          <Box
            sx={{
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              borderRadius: "50%",
              backgroundColor: "#fee",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarningIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            {isMobile ? "Incidencia" : "Gestión de Incidencia"}
          </Typography>
        </Box>

        <IconButton onClick={cerrar} disabled={loading} size="small">
          <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      {/* DECISION: se deja el flex por defecto de MUI (1 1 auto) + overflow-y auto.
          El override flex:"initial" en desktop provocaba doble scroll (contenido +
          contenedor del diálogo) cuando el contenido es largo (comentarios + formulario). */}
      <DialogContent
        sx={{
          pt: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 2 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Información de la incidencia */}
        <SeccionInformacion texto={infoText} />

        {/* Tramitado/Creado por: solo si se puede editar */}
        {puedeEditar && (
          <SeccionEmpleado
            label={labelEmpleado}
            empleadoId={empleadoId}
            empleadoNombre={empleadoNombre}
            empleados={empleados}
            esAdmin={esAdmin}
            loading={loading}
            onChange={handleEmpleadoChange}
          />
        )}

        {/* Estado */}
        <SeccionEstado
          estado={estado}
          puedeEditar={puedeEditarEstado}
          loading={loading}
          onChange={(e) => setEstado(e.target.value)}
        />

        <Divider sx={{ my: 2 }} />

        {/* Comentarios anteriores */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: "#495057",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <ForumIcon sx={{ fontSize: 18 }} />
            Historial de comentarios
          </Typography>
          <ComentariosList comentarios={data.comentarios} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Nuevo comentario (solo si puede editar) */}
        {puedeEditar && (
          <SeccionComentarioNuevo
            comentario={comentario}
            error={error}
            loading={loading}
            onChange={handleComentarioChange}
            onImagenesChange={setImagenes}
            puedeEnviarEmail={puedeEnviarEmail}
            destinatarioEmail={clienteFactura?.NOMBRE?.trim()}
            enviarEmail={enviarEmail}
            onEnviarEmailChange={setEnviarEmail}
          />
        )}
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions
        sx={{
          p: { xs: 2, sm: 2.5 },
          gap: { xs: 1, sm: 1.5 },
          flexDirection: { xs: "column-reverse", sm: "row" },
        }}
      >
        <Button
          variant="outlined"
          onClick={cerrar}
          fullWidth={isMobile}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.25, sm: 1 },
            fontSize: { xs: 14, sm: 15 },
            color: "#6c757d",
            borderColor: "#6c757d",
            "&:hover": {
              borderColor: "#495057",
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          Cerrar
        </Button>

        {puedeEditar && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            fullWidth={isMobile}
            disabled={loading || !comentario.trim()}
            startIcon={
              loading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <SaveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.25, sm: 1 },
              fontSize: { xs: 14, sm: 15 },
              backgroundColor: "#cc071e",
              "&:hover": {
                backgroundColor: "#a30618",
              },
              "&:disabled": {
                backgroundColor: "#6c757d",
                color: "#fff",
              },
            }}
          >
            {loading ? "Guardando..." : labelGuardar}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalIncidencia;
